import React, { useState, useCallback } from 'react';

import { translation, formatToReactComponent, TranslatedComponent } from '@hh.ru/front-static-app';
import { useSelectorNonNullable } from '@hh.ru/redux-create-reducer';
import { SPALink, usePush } from '@hh.ru/redux-spa-middleware';
import Button, { ButtonKind, ButtonType } from 'bloko/blocks/button';
import Column from 'bloko/blocks/column';
import { FormError, FormRequired } from 'bloko/blocks/form';
import Input, { InputType } from 'bloko/blocks/inputText';
import BlokoLink from 'bloko/blocks/link';
import Radio from 'bloko/blocks/radio';
import Text, { TextSize, TextImportance } from 'bloko/blocks/text';
import Textarea from 'bloko/blocks/textarea';
import VSpacing from 'bloko/blocks/vSpacing';
import urlParser from 'bloko/common/urlParser';

import Form from 'src/components/Form';
import { useChatikLink } from 'src/hooks/useChatikLink';
import useIsClient from 'src/hooks/useIsClient';
import UserTypes from 'src/types/userTypes';
import fetcher from 'src/utils/fetcher';

import RequestPlanerkaFrame from 'src/pages/ApplicantServicesOrder/components/CareerService/RequestPlanerkaFrame';

import styles from './index.less';

const URL = '/applicant-services/complete_resume/planerka/' as const;
const URL_BACKOFFICE = '/admin/complete_resume/order/' as const;

declare global {
    interface FetcherPostApi {
        [k: `${typeof URL}${string}`]: {
            body: FormData;
            queryParams: void;
            response: { planerkaSrc?: string };
        };
    }
}

const TrlKeys = {
    resumeList: 'applicantServices.createRequest.resumelist',
    link: 'applicantServices.createRequest.link',
    information: 'applicantServices.createRequest.information',
    informationMore: 'applicantServices.createRequest.informationMore',
    email: 'applicantServices.createRequest.email',
    phone: 'applicantServices.createRequest.phone',
    sendRequest: 'applicantServices.createRequest.sendRequest',
    backError: 'applicantServices.createRequest.backError',
    selectDateTime: 'applicantServices.createRequest.selectDateTime',
    chatikText: 'applicantServices.order.createRequest.chatik.text',
    chatikLink: 'applicantServices.order.chatik.link',
};

const RequestPlanerkaForm: TranslatedComponent<{ isEdit?: boolean }> = ({ isEdit, trls }) => {
    const isWebMobile = useSelectorNonNullable((state) => state.isWebMobile);
    const userType = useSelectorNonNullable((state) => state.userType);
    const pathname = useSelectorNonNullable((state) => state.router.location.pathname);
    const order = useSelectorNonNullable(({ careerServiceOrder }) => careerServiceOrder);
    const resumes = useSelectorNonNullable((state) => state.resumeIdentifiers);
    const disableChangeEmail = !!order.email;
    const [inputDescriptionValue, setInputDescriptionValue] = useState(
        isEdit || order.description ? order.description : ''
    );
    const { phone, email } = isEdit ? order : order.userContacts;
    const [inputEmailValue, setInputEmailValue] = useState(email);
    const [inputPhoneValue, setInputPhoneValue] = useState(phone);
    const [hasError, setHasError] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [planerkaSrc, setPlanerkaSrc] = useState('');
    const isClient = useIsClient();
    const push = usePush();
    const getChatikLink = useChatikLink();

    const onSubmit = useCallback(
        (submitEvent: React.FormEvent<HTMLFormElement>) => {
            submitEvent.preventDefault();
            const formData = new FormData(submitEvent.currentTarget);
            setHasError(false);
            setFetching(true);

            fetcher
                .postFormData(`${URL}${order.orderCode}`, formData)
                .then((data) => {
                    window.scrollTo(0, 0);
                    if (data.data.planerkaSrc) {
                        let planerkaUrl = data.data.planerkaSrc;
                        if (isWebMobile) {
                            const url = urlParser(planerkaUrl);
                            url.params = {
                                ...url.params,
                                disableExtendedCalendarBtn: 1, // выключаем кнопки календарей на мобилках
                            };
                            planerkaUrl = url.href;
                        }
                        setPlanerkaSrc(planerkaUrl);
                    }
                    if (isEdit) {
                        if (userType === UserTypes.Backoffice) {
                            push(`${URL_BACKOFFICE}${order.orderCode}?applicant_id=${order.applicant.id}`);
                        } else {
                            push(pathname.replace('/edit', ''));
                        }
                    }
                })
                .catch(() => {
                    setHasError(true);
                })
                .finally(() => {
                    setFetching(false);
                });
        },
        [order.orderCode, order.applicant.id, isEdit, isWebMobile, userType, push, pathname]
    );
    return (
        <>
            {planerkaSrc ? (
                <>
                    <RequestPlanerkaFrame src={planerkaSrc} />
                </>
            ) : (
                <div className={styles.formContainer}>
                    <VSpacing base={5} />
                    <Form onSubmit={onSubmit}>
                        <input type="hidden" name="create" value={String(!isEdit)} />
                        <input type="hidden" name="step" value={isEdit ? 'awaiting' : 'request'} />
                        <VSpacing base={4} />
                        <div>
                            <Column xs="4" s="6" m="3" l="4">
                                <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.resumeList]}</Text>
                            </Column>
                            <Column xs="4" s="6" m="6" l="7">
                                {resumes.map((resume, index) => (
                                    <div key={resume.id}>
                                        <Radio
                                            name="resumeId"
                                            value={String(resume.id)}
                                            defaultChecked={isEdit ? resume.hash === order.resumeHash : index === 0}
                                        >
                                            <Text>{resume.title}</Text>
                                            <Text size={TextSize.Small}>
                                                <BlokoLink
                                                    Element={SPALink}
                                                    to={`/resume/${resume.hash}`}
                                                    target="_blank"
                                                    disableVisited
                                                    data-qa="expert-service-resume"
                                                >
                                                    {trls[TrlKeys.link]}
                                                </BlokoLink>
                                            </Text>
                                            <VSpacing base={2} />
                                        </Radio>
                                    </div>
                                ))}
                            </Column>
                        </div>
                        <VSpacing base={6} />
                        <div>
                            <Column xs="4" s="6" m="3" l="4">
                                <Text importance={TextImportance.Tertiary}>
                                    {trls[TrlKeys.information]} <FormRequired />
                                </Text>
                                <Text importance={TextImportance.Tertiary}>{trls[TrlKeys.informationMore]}</Text>
                            </Column>
                            <Column xs="4" s="6" m="6" l="7">
                                <Textarea
                                    name="description"
                                    id="description"
                                    data-qa="description-field"
                                    rows={10}
                                    value={inputDescriptionValue}
                                    invalid={!inputDescriptionValue}
                                    onChange={(event) => {
                                        setInputDescriptionValue(event.target.value);
                                    }}
                                />
                            </Column>
                        </div>
                        <VSpacing base={6} />
                        <div>
                            <Column xs="4" s="6" m="3" l="4">
                                <Text importance={TextImportance.Tertiary}>
                                    {trls[TrlKeys.email]} <FormRequired />
                                </Text>
                            </Column>
                            <Column xs="4" s="6" m="6" l="7">
                                <Input
                                    type={InputType.Email}
                                    name="email"
                                    id="email"
                                    size={30}
                                    data-qa="account-signup-email"
                                    value={inputEmailValue}
                                    invalid={!inputEmailValue}
                                    disabled={disableChangeEmail}
                                    onChange={setInputEmailValue}
                                />
                            </Column>
                        </div>
                        <VSpacing base={6} />
                        <div>
                            <Column xs="4" s="6" m="3" l="4">
                                <Text importance={TextImportance.Tertiary}>
                                    {trls[TrlKeys.phone]} <FormRequired />
                                </Text>
                            </Column>
                            <Column xs="4" s="6" m="6" l="7">
                                <Input
                                    type={InputType.Tel}
                                    name="phone"
                                    id="phone"
                                    size={30}
                                    value={inputPhoneValue}
                                    invalid={!inputPhoneValue || inputPhoneValue.trim() === ''}
                                    onChange={setInputPhoneValue}
                                    data-qa="phone-field"
                                />
                            </Column>
                        </div>
                        <VSpacing base={6} />
                        {order.chatId && (
                            <>
                                <div>
                                    <Column xs="4" s="6" m="3" l="4"></Column>
                                    <Column xs="4" s="6" m="6" l="7">
                                        {formatToReactComponent(trls[TrlKeys.chatikText], {
                                            '{0}': (
                                                <SPALink target="_blank" to={getChatikLink(order.chatId)} inline>
                                                    {trls[TrlKeys.chatikLink]}
                                                </SPALink>
                                            ),
                                        })}
                                    </Column>
                                </div>
                                <VSpacing base={6} />
                            </>
                        )}
                        <div>
                            <Column xs="4" s="6" m="3" l="4"></Column>
                            <Column xs="4" s="6" m="6" l="7">
                                <Button
                                    kind={ButtonKind.Primary}
                                    type={ButtonType.Submit}
                                    data-qa="send-button"
                                    disabled={
                                        !isClient ||
                                        !inputDescriptionValue ||
                                        !inputEmailValue ||
                                        !inputPhoneValue ||
                                        inputPhoneValue.trim() === '' ||
                                        fetching
                                    }
                                >
                                    {isEdit ? trls[TrlKeys.sendRequest] : trls[TrlKeys.selectDateTime]}
                                </Button>
                                <FormError show={hasError}>
                                    <VSpacing base={2} />
                                    {trls[TrlKeys.backError]}
                                </FormError>
                            </Column>
                        </div>
                    </Form>
                    <VSpacing base={5} />
                </div>
            )}
        </>
    );
};

export default translation(RequestPlanerkaForm);
